import React, { useState, useMemo, useEffect } from "react";
import { Table, Input } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import { Package, Search } from "lucide-react";
import { ReportPage } from "../../../components";
import { GetInventoryReportProductStock } from "../../../Api";
import { DEBOUNCED_SEARCH_QUERY } from "../../../Constant";
import { exportToCSV, formatLKR } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

export const ProductStockReport: React.FC = () => {
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["product-stock-report", search],
    queryFn: () =>
      GetInventoryReportProductStock({
        search,
      }),
  });

  const reportData = useMemo(() => responseData?.data || [], [responseData]);
  const { isAllowed } = useSectionAccess("reports");

  const stats = useMemo(() => {
    const itemsCount = reportData.length;
    const stockSum = reportData.reduce(
      (sum: number, item: any) => sum + (item.total_quantity || 0),
      0,
    );
    const costVal = reportData.reduce(
      (sum: number, item: any) => sum + (item.total_cost_value || 0),
      0,
    );
    const retailVal = reportData.reduce(
      (sum: number, item: any) => sum + (item.total_retail_value || 0),
      0,
    );
    return [
      {
        title: "Total Distinct Products",
        value: `${itemsCount} Items`,
        colorClass: "text-slate-655",
      },
      {
        title: "Total Stock Volume",
        value: `${stockSum} Units`,
        colorClass: "text-indigo-600",
      },
      {
        title: "Valuation (Cost Rate)",
        value: formatLKR(costVal),
        colorClass: "text-gray-700",
      },
      {
        title: "Valuation (Retail Rate)",
        value: formatLKR(retailVal),
        colorClass: "text-indigo-700 font-extrabold",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Product Title",
      dataIndex: "title",
      key: "title",
      render: (v: string, record: any) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-bold text-gray-800">{v}</span>
          <span className="text-[10px] text-gray-400 font-medium">
            SKU: {record.sku_code} | Barcode: {record.barcode}
          </span>
        </div>
      ),
    },
    {
      title: "Category / Brand",
      dataIndex: "category_name",
      key: "category_name",
      render: (v: string, record: any) => (
        <div className="flex flex-col gap-0.5 text-xs text-gray-650 font-medium">
          <span>{v}</span>
          <span className="text-[10px] text-gray-400">{record.brand_name}</span>
        </div>
      ),
    },
    {
      title: "Stock Qty",
      dataIndex: "total_quantity",
      key: "total_quantity",
      align: "center" as const,
      render: (v: number, record: any) => (
        <span
          className={`text-xs font-bold ${v <= 10 ? "text-rose-600 bg-rose-50 border border-rose-100 rounded px-2 py-0.5" : "text-gray-755"}`}
        >
          {v} {record.unit}
        </span>
      ),
    },
    {
      title: "Avg Cost",
      dataIndex: "avg_cost_price",
      key: "avg_cost_price",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-semibold text-gray-500">
          LKR {v.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Avg Retail",
      dataIndex: "avg_retail_price",
      key: "avg_retail_price",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-semibold text-gray-770">
          LKR {v.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Valuation (Cost)",
      dataIndex: "total_cost_value",
      key: "total_cost_value",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-extrabold text-gray-900">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Valuation (Retail)",
      dataIndex: "total_retail_value",
      key: "total_retail_value",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-extrabold text-indigo-755">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
  ];

  const filterControls = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-col gap-1.5 font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Search Records
        </span>
        <Input
          placeholder="Search product title, SKU, barcode..."
          prefix={<Search className="w-3.5 h-3.5 text-gray-450 mr-1.5" />}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          allowClear
          className="w-72 h-9 rounded-lg text-xs"
        />
      </div>
    </div>
  );

  return (
    <ReportPage
      title="Product Stock Report"
      description="Check current quantity of products, average cost, average retail price, and total valuation."
      icon={<Package className="w-6 h-6 text-indigo-600" />}
      backPath="/system/inventory-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "cash_flow_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
      filterControls={filterControls}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) => record.sku_code || Math.random().toString()}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
